// deadlineCompute.js — refactor 2026-05-21.
//
// Helper compute danh sách deadline cho 1 row đề tài (SV / Cơ sở) dựa trên:
//   - row.endTime              (column trên đề tài)
//   - yearlyDueDate            (cấu hình mốc BCTĐ + nghiệm thu / năm học)
//   - danh sách kinds          (DEADLINE_KINDS_SR / IR từ constants/deadlineKinds.js)
//
// Trả về array `{ kind, label, short, date, daysLeft, bucket }` đã sort
// theo daysLeft ASC (overdue lên đầu). Filter UI dùng kết quả này để bucket
// chip + render cell badge + tooltip.

import dayjs from "dayjs";
import { bucketOfDaysLeft, BUCKET_ORDER } from "../constants/deadlineKinds";

// Lấy date portion từ ISO/Date/string. Trả "YYYY-MM-DD" hoặc null.
function isoDate(v) {
  if (!v) return null;
  const s = String(v).substring(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

// dispatchSource — đọc giá trị deadline theo source key của kind.
// source: "endTime" hoặc "yearlyDue.<field>".
function dispatchSource(source, row, dueDate) {
  if (!source) return null;
  // Đề tài Cơ sở đã được duyệt gia hạn (extendedUntilDate) → hạn hiệu lực là
  // ngày gia hạn, không phải endTime gốc. Ưu tiên extendedUntilDate nếu có.
  if (source === "endTime") return isoDate(row.extendedUntilDate || row.endTime);
  if (source.startsWith("yearlyDue.")) {
    if (!dueDate) return null;
    const field = source.split(".")[1];
    return isoDate(dueDate[field]);
  }
  return null;
}

// daysBetween — số ngày (deadline - today). Dương = trước, âm = quá.
function daysBetween(targetISO, todayISO) {
  return dayjs(targetISO).diff(dayjs(todayISO), "day");
}

/**
 * computeDeadlines(row, dueDate, kinds) → DeadlineInfo[]
 *
 * @param {object} row     — row đề tài (đã có endTime, status, durationMonths,…).
 * @param {object} dueDate — record YearlyDueDate của năm học (hoặc null nếu chưa load).
 * @param {Array}  kinds   — DEADLINE_KINDS_SR hoặc DEADLINE_KINDS_IR.
 * @returns Array<{ kind, label, short, date, daysLeft, bucket }>
 *          Đã sort daysLeft ASC (worst first), chỉ gồm kind `applies(row) === true`
 *          và có date hợp lệ.
 */
export function computeDeadlines(row, dueDate, kinds) {
  if (!row || !Array.isArray(kinds)) return [];
  const today = dayjs().format("YYYY-MM-DD");
  const out = [];
  for (const k of kinds) {
    if (typeof k.applies === "function" && !k.applies(row)) continue;
    const date = dispatchSource(k.source, row, dueDate);
    if (!date) continue;
    const daysLeft = daysBetween(date, today);
    out.push({
      kind: k.key,
      label: k.label,
      short: k.short,
      date,
      daysLeft,
      bucket: bucketOfDaysLeft(daysLeft),
    });
  }
  out.sort((a, b) => a.daysLeft - b.daysLeft);
  return out;
}

/**
 * worstBucket — trong nhiều deadline, lấy bucket nguy hiểm nhất (overdue > soon > …).
 * Trả về "none" nếu list rỗng.
 */
export function worstBucket(deadlines) {
  if (!deadlines || deadlines.length === 0) return "none";
  for (const b of BUCKET_ORDER) {
    if (deadlines.some((d) => d.bucket === b)) return b;
  }
  return "none";
}

/**
 * rowMatchesFilter — row có deadline nào rơi vào filter chip không.
 *
 * @param {Array}  deadlines  — output computeDeadlines.
 * @param {string} chip       — "all" | "overdue" | "soon7" | "soon30".
 * @param {string} kindKey    — "all" hoặc kind.key cụ thể (drill-down).
 */
export function rowMatchesFilter(deadlines, chip, kindKey) {
  let list = deadlines;
  if (kindKey && kindKey !== "all") {
    list = deadlines.filter((d) => d.kind === kindKey);
  }
  if (chip === "all" || !chip) return true; // không filter theo urgency
  if (chip === "overdue") return list.some((d) => d.bucket === "overdue");
  if (chip === "soon7") return list.some((d) => d.bucket === "overdue" || d.bucket === "soon");
  if (chip === "soon30") return list.some((d) => ["overdue", "soon", "near"].includes(d.bucket));
  return true;
}

/**
 * formatDaysLeft — string ngắn gọn cho cell.
 *   -3 → "Quá 3d"
 *    0 → "Hôm nay"
 *    5 → "Còn 5d"
 *   45 → "Còn 45d"
 */
export function formatDaysLeft(daysLeft) {
  if (daysLeft === null || daysLeft === undefined || Number.isNaN(daysLeft)) return "—";
  if (daysLeft < 0) return `Quá ${-daysLeft}d`;
  if (daysLeft === 0) return "Hôm nay";
  return `Còn ${daysLeft}d`;
}

/**
 * formatDateVN — "YYYY-MM-DD" → "DD/MM/YYYY"
 */
export function formatDateVN(iso) {
  if (!iso) return "—";
  const s = String(iso).substring(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
}
